import { Modal, BodyLargeEmphasized } from '@/design-system';
import { GradeField, GradeRangeSelector } from '@/components';
import { FilterOption, OptionType } from '@/components/FlyerboardFilters/FilterOption';
import { Divider } from '@/components/Divider';
import { css } from '@emotion/css';

type JarFilterModalProps = {
  isOpen: boolean;
  onToggle: (state: boolean) => void;
  filters: {
    cost: string[];
    provider: string[];
    types: string[];
  };
  setFilters: (filters: any) => void;
  gradeFilter: { from?: string; to?: string };
  setGradeFilter: (gradeFilter: any) => void;
  loading: boolean;
  viewResultsLabel: string;
  resetFilters: () => void;
  viewResults: () => void;
  handleToggleOption: (section: string, value: string) => void;
  handleGradeChange: (field: 'from' | 'to', value?: string) => void;
  generateProviderOptions: () => OptionType[];
  generateCostOption: () => OptionType[];
  generateTypesOptions: () => OptionType[];
  disableClearAllButton: boolean;
  grades: any[];
  lowGrade: string;
  highGrade: string;
  setDefaultGrades: () => void;
};

export const JarFilterModal: React.FC<JarFilterModalProps> = (props) => {
  const {
    isOpen,
    onToggle,
    filters,
    gradeFilter,
    loading,
    viewResultsLabel,
    resetFilters,
    viewResults,
    handleToggleOption,
    handleGradeChange,
    generateProviderOptions,
    generateCostOption,
    generateTypesOptions,
    disableClearAllButton,
    grades,
  } = props;

  const costOptions = generateCostOption();
  const providerOptions = generateProviderOptions();
  const typesOptions = generateTypesOptions();
  return (
    <Modal
      isOpen={isOpen}
      title="Filters"
      confirmButtonText={viewResultsLabel}
      cancelButtonText="Reset Filters"
      onCancel={resetFilters}
      onConfirm={viewResults}
      onClose={() => onToggle(false)}
      disableCancelButton={disableClearAllButton}
      loading={loading}
      className={styles.modal}
    >
      <div className={styles.container}>
        <div aria-labelledby="filter-provider-label" role="group">
          <BodyLargeEmphasized className={styles.filterName} id="filter-provider-label">
            Provider
          </BodyLargeEmphasized>
          <div className={styles.filtersContainer}>
            <div className={styles.flexColumn}>
              {providerOptions.slice(0, Math.ceil(providerOptions.length / 2)).map(({ value, description, disabled = false }) => {
                const isChecked = filters.provider.includes(value);
                return (
                  <FilterOption
                    key={value}
                    description={description}
                    value={value}
                    filterName="provider"
                    checked={isChecked}
                    handleToggle={() => () => handleToggleOption('provider', value)}
                    disabled={disabled && !isChecked}
                  />
                );
              })}
            </div>
            <div className={styles.flexColumn}>
              {providerOptions.slice(Math.ceil(providerOptions.length / 2)).map(({ value, description, disabled = false }) => {
                const isChecked = filters.provider.includes(value);
                return (
                  <FilterOption
                    key={value}
                    description={description}
                    value={value}
                    filterName="provider"
                    checked={isChecked}
                    handleToggle={() => () => handleToggleOption('provider', value)}
                    disabled={disabled && !isChecked}
                  />
                );
              })}
            </div>
          </div>
          <div className={styles.divider}>
            <Divider />
          </div>
        </div>
        {costOptions.length > 0 && (
          <div aria-labelledby="filter-cost-label" role="group">
            <BodyLargeEmphasized className={styles.filterName} id="filter-cost-label">
              Cost
            </BodyLargeEmphasized>
            <div className={styles.optionsContainer}>
              {costOptions.map(({ value, description, disabled = false }) => {
                const isChecked = filters.cost.includes(value);
                return (
                  <FilterOption
                    key={value}
                    description={description}
                    value={value}
                    filterName="cost"
                    checked={isChecked}
                    handleToggle={() => () => handleToggleOption('cost', value)}
                    disabled={disabled && !isChecked}
                  />
                );
              })}
            </div>
            <div className={styles.divider}>
              <Divider />
            </div>
          </div>
        )}
        <div aria-labelledby="filter-grade-label" role="group">
          <BodyLargeEmphasized className={styles.filterName} id="filter-grade-label">
            Grade
          </BodyLargeEmphasized>
          <div className={styles.gradeSelectorContainer}>
            <GradeRangeSelector
              fromGrade={gradeFilter.from}
              toGrade={gradeFilter.to}
              onChange={handleGradeChange}
              grades={grades}
            />
          </div>
          <div className={styles.divider}>
            <Divider />
          </div>
        </div>

        <div aria-labelledby="filter-types-label" role="group">
          <BodyLargeEmphasized className={styles.filterName} id="filter-types-label">
            Types
          </BodyLargeEmphasized>
          <div className={styles.filtersContainer}>
            <div className={styles.flexColumn}>
              {typesOptions.slice(0, Math.ceil(typesOptions.length / 2)).map(({ value, description }) => {
                const isChecked = filters.types.includes(value);
                return (
                  <FilterOption
                    key={value}
                    description={description}
                    value={value}
                    filterName="types"
                    checked={isChecked}
                    handleToggle={() => () => handleToggleOption('types', value)}
                  />
                );
              })}
            </div>
            <div className={styles.flexColumn}>
              {typesOptions.slice(Math.ceil(typesOptions.length / 2)).map(({ value, description }) => {
                const isChecked = filters.types.includes(value);
                return (
                  <FilterOption
                    key={value}
                    description={description}
                    value={value}
                    filterName="types"
                    checked={isChecked}
                    handleToggle={() => () => handleToggleOption('types', value)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
  `,
  filterName: css`
    text-transform: capitalize;
  `,
  optionsContainer: css`
    padding-top: 16px;
  `,
  gradeSelectorContainer: css`
    padding-top: 16px;
  `,
  filtersContainer: css`
    display: flex;
    flex-wrap: wrap;
    padding-top: 16px;
  `,
  divider: css`
    padding: 24px 0;
  `,
  flexColumn: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding-right: 10px;

    &:last-child {
      padding-right: 0;
    }
  `,
  modal: css`
    width: 680px;
  `,
};
